import { Controller } from "@hotwired/stimulus"

/**
 * ContactFormController
 * 
 * Handles the public contact form with:
 * - reCAPTCHA v2 checkbox integration (Turbo-compatible)
 * - Client-side validation with visual feedback
 * - Honeypot field hiding for bot detection
 * 
 * Usage:
 *   data-controller="contact-form"
 *   data-contact-form-sitekey-value="<%= Recaptcha.configuration.site_key %>"
 *   data-action="submit->contact-form#validate"
 */

const RECAPTCHA_SCRIPT_URL = 'https://www.google.com/recaptcha/api.js'
const RECAPTCHA_CALLBACK = '__contactFormRecaptchaCallback'
const RECAPTCHA_POLL_INTERVAL_MS = 100

export default class extends Controller {
  static targets = ["input", "recaptchaContainer"]
  static values = { sitekey: String }

  // ==========================================================================
  // Lifecycle
  // ==========================================================================

  connect() {
    this.hideHoneypot()
    this.initRecaptcha()
  }

  // ==========================================================================
  // Honeypot (Bot Detection)
  // ==========================================================================

  hideHoneypot() {
    const honeypot = this.element.querySelector('.honeypot-field')
    if (honeypot) {
      honeypot.style.display = 'none'
    }
  }

  // ==========================================================================
  // reCAPTCHA - Turbo-compatible explicit rendering
  // ==========================================================================

  initRecaptcha() {
    if (!this.hasRecaptchaContainerTarget || !this.sitekeyValue) return

    if (this.isGrecaptchaReady()) {
      this.renderRecaptchaWidget()
    } else if (this.isRecaptchaScriptInjected()) {
      this.waitForGrecaptcha()
    } else {
      this.injectRecaptchaScript()
    }
  }

  isGrecaptchaReady() {
    return typeof grecaptcha !== 'undefined' && typeof grecaptcha.render === 'function'
  }

  isRecaptchaScriptInjected() {
    return !!document.querySelector(`script[src*="recaptcha/api.js"]`)
  }

  injectRecaptchaScript() {
    window[RECAPTCHA_CALLBACK] = () => this.renderRecaptchaWidget()

    const script = document.createElement('script')
    script.src = `${RECAPTCHA_SCRIPT_URL}?onload=${RECAPTCHA_CALLBACK}&render=explicit`
    script.async = true
    script.defer = true
    document.head.appendChild(script)
  }

  waitForGrecaptcha() {
    const poll = () => {
      if (this.isGrecaptchaReady()) {
        this.renderRecaptchaWidget()
      } else {
        setTimeout(poll, RECAPTCHA_POLL_INTERVAL_MS)
      }
    }
    poll()
  }

  renderRecaptchaWidget() {
    const container = this.recaptchaContainerTarget
    container.innerHTML = ''

    try {
      grecaptcha.render(container, { sitekey: this.sitekeyValue })
    } catch (error) {
      // Widget may already exist with same container ID
      this.tryResetRecaptcha(error)
    }
  }

  tryResetRecaptcha(originalError) {
    try {
      grecaptcha.reset()
    } catch (resetError) {
      console.debug('reCAPTCHA initialization failed:', originalError.message)
    }
  }

  // ==========================================================================
  // Form Validation
  // ==========================================================================

  validate(event) {
    const invalidFields = this.validateRequiredFields()
    
    if (invalidFields.length > 0) {
      event.preventDefault()
      event.stopPropagation()
      this.showValidationError()
      this.scrollToTop()
      this.reenableSubmitButton()
    }
  }

  validateRequiredFields() {
    const requiredInputs = this.element.querySelectorAll('.form-control.required, .form-select.required')
    const invalidFields = []

    requiredInputs.forEach(input => {
      const formGroup = input.closest('.mb-3') || input.parentElement
      const isEmpty = !input.value.trim()

      if (isEmpty) {
        invalidFields.push(input)
        formGroup?.classList.add('has-danger')
      } else {
        formGroup?.classList.remove('has-danger')
      }
    })

    return invalidFields
  }

  showValidationError() {
    const warningHolder = document.querySelector('.warning-holder')
    if (!warningHolder) return

    // Remove any existing warning from this controller
    warningHolder.querySelector('.contact-form-warning')?.remove()

    const message = document.createElement('div')
    message.className = 'warning-message contact-form-warning'
    message.textContent = "We couldn't save your information. Please fix the highlighted fields."
    warningHolder.appendChild(message)
  }

  scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  reenableSubmitButton() {
    const submitBtn = this.element.querySelector('input[type="submit"], button[type="submit"]')
    if (submitBtn) {
      setTimeout(() => { submitBtn.disabled = false }, 100)
    }
  }
}
